import { useEffect, useMemo, useState, useCallback } from "react";
import { useDogs } from "@/context/DogsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SectionTitle } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import DogRunAnimation from "@/components/app/DogRunAnimation";
import AllDogsMonthCalendar from "@/components/app/AllDogsMonthCalendar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChevronLeft, ChevronRight, Plus, Trash2, Scissors, Dumbbell, Stethoscope, Tag, NotebookPen, Sun } from "lucide-react";
import { format, parseISO, startOfWeek, addDays, subWeeks, addWeeks, isToday } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from "recharts";
import { toast } from "sonner";
import { ACTIVITY_GROUPS, ACTIVITIES, mergeCustomActivities } from "@/lib/activities";
import CustomActivitiesDialog from "@/components/app/CustomActivitiesDialog";

const EVENT_TYPES = [
  { value: "grooming", label: "Grooming", icon: Scissors, color: "hsl(120 13% 20%)" },
  { value: "training", label: "Training", icon: Dumbbell, color: "hsl(36 25% 55%)" },
  { value: "vet", label: "Vet", icon: Stethoscope, color: "hsl(17 41% 44%)" },
  { value: "other", label: "Other", icon: Tag, color: "hsl(197 37% 24%)" },
];

// Flat list with synthetic keys used elsewhere in the planner.
const ACTIVITIES_WITH_KEYS = ACTIVITIES.map((it) => ({ ...it, key: it.label.toLowerCase().replace(/[^a-z0-9]+/g, "_") }));
export { ACTIVITIES_WITH_KEYS };

export default function WeeklyPlannerPage() {
  const { activeDog } = useDogs();
  const [view, setView] = useState(() => localStorage.getItem("weekly_view") || "this-dog"); // "this-dog" | "all-dogs"
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [events, setEvents] = useState([]);
  const [dailyEntries, setDailyEntries] = useState([]);
  const [journal, setJournal] = useState("");
  const [savingJournal, setSavingJournal] = useState(false);

  // Day dialog
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dailyText, setDailyText] = useState("");
  const [savingDaily, setSavingDaily] = useState(false);
  const [eventForm, setEventForm] = useState({ activity: "", notes: "" });
  const [showEventForm, setShowEventForm] = useState(false);
  const [runTick, setRunTick] = useState(0);

  // Today spotlight state (inline editing, no dialog)
  const today = useMemo(() => new Date(), []);
  const todayStr = format(today, "yyyy-MM-dd");
  const [todayNote, setTodayNote] = useState("");
  const [savingTodayNote, setSavingTodayNote] = useState(false);
  const [todayActivity, setTodayActivity] = useState(ACTIVITIES[0]?.label || "");

  // Per-dog custom activities loaded from /api/dogs/{id}/custom-activities.
  // Merged into the built-in group list so they appear in every dropdown
  // (Today's activities, event form). Refreshed via `reloadCustomActivities`
  // after the manage-dialog closes.
  const [customActivities, setCustomActivities] = useState([]);
  const [pinnedLabels, setPinnedLabels] = useState([]);
  const [manageActivitiesOpen, setManageActivitiesOpen] = useState(false);
  const { groups: mergedGroups, flat: mergedActivities } = useMemo(
    () => mergeCustomActivities(customActivities, pinnedLabels),
    [customActivities, pinnedLabels],
  );

  const reloadCustomActivities = useCallback(async () => {
    if (!activeDog?.id) { setCustomActivities([]); setPinnedLabels([]); return; }
    try {
      const [customRes, pinnedRes] = await Promise.all([
        api.get(`/dogs/${activeDog.id}/custom-activities`),
        api.get(`/dogs/${activeDog.id}/pinned-activities`),
      ]);
      setCustomActivities(customRes.data.items || []);
      setPinnedLabels((pinnedRes.data.items || []).map((p) => p.label));
    } catch {
      setCustomActivities([]);
      setPinnedLabels([]);
    }
  }, [activeDog?.id]);

  useEffect(() => { reloadCustomActivities(); }, [reloadCustomActivities]);

  // Keep the today / event-form selected activity in sync with the available list
  useEffect(() => {
    if (mergedActivities.length === 0) return;
    if (!mergedActivities.find((a) => a.label === todayActivity)) {
      setTodayActivity(mergedActivities[0].label);
    }
    if (!mergedActivities.find((a) => a.label === eventForm.activity)) {
      setEventForm((f) => ({ ...f, activity: mergedActivities[0].label }));
    }
  }, [mergedActivities, todayActivity, eventForm.activity]);

  const weekStartStr = format(weekStart, "yyyy-MM-dd");
  const weekEnd = addDays(weekStart, 6);
  const weekEndStr = format(weekEnd, "yyyy-MM-dd");
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = async () => {
    if (!activeDog) return;
    const year = weekStart.getFullYear();
    // events for any month touched by this week
    const months = [...new Set([weekStart.getMonth() + 1, weekEnd.getMonth() + 1])];
    const evResponses = await Promise.all(
      months.map((m) => api.get("/events", { params: { dog_id: activeDog.id, year: addDays(weekStart, 6).getFullYear(), month: m } }))
    );
    const allEvents = evResponses.flatMap((r) => r.data.items || []);
    const inWeek = allEvents.filter((ev) => ev.event_date >= weekStartStr && ev.event_date <= weekEndStr);
    setEvents(inWeek);

    const [deRes, jRes] = await Promise.all([
      api.get("/daily-entries", { params: { dog_id: activeDog.id, start: weekStartStr, end: weekEndStr } }),
      api.get("/weekly-journal", { params: { dog_id: activeDog.id, week_start: weekStartStr } }),
    ]);
    setDailyEntries(deRes.data.items || []);
    setJournal(jRes.data.content || "");

    // Today's daily note (may be inside or outside the visible week)
    const todayInWeek = (deRes.data.items || []).find((e) => e.entry_date === todayStr);
    setTodayNote(todayInWeek?.content || "");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeDog, weekStartStr]);

  const eventsByDate = useMemo(() => events.reduce((acc, e) => {
    (acc[e.event_date] = acc[e.event_date] || []).push(e); return acc;
  }, {}), [events]);
  const entryByDate = useMemo(() => dailyEntries.reduce((acc, e) => {
    acc[e.entry_date] = e; return acc;
  }, {}), [dailyEntries]);

  // ---- Visualization data ----
  const vizData = useMemo(() => {
    return days.map((d) => {
      const ds = format(d, "yyyy-MM-dd");
      const dayEvents = eventsByDate[ds] || [];
      return {
        day: format(d, "EEE"),
        date: ds,
        events: dayEvents.length,
        note: entryByDate[ds]?.content ? 1 : 0,
        grooming: dayEvents.filter((e) => e.event_type === "grooming").length,
        training: dayEvents.filter((e) => e.event_type === "training").length,
        vet: dayEvents.filter((e) => e.event_type === "vet").length,
        other: dayEvents.filter((e) => e.event_type === "other").length,
      };
    });
  }, [days, eventsByDate, entryByDate]);

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;

  const setViewMode = (v) => {
    setView(v);
    localStorage.setItem("weekly_view", v);
  };

  const onDayClick = (d) => {
    const ds = format(d, "yyyy-MM-dd");
    setSelectedDate(d);
    setDailyText(entryByDate[ds]?.content || "");
    setEventForm({ activity: ACTIVITIES[0].label, notes: "" });
    setShowEventForm(false);
    setOpen(true);
  };

  const saveDaily = async () => {
    setSavingDaily(true);
    try {
      await api.post("/daily-entries", {
        dog_id: activeDog.id,
        entry_date: format(selectedDate, "yyyy-MM-dd"),
        content: dailyText,
      });
      toast.success(dailyText.trim() ? "Daily note saved" : "Daily note cleared");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSavingDaily(false);
    }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.activity) return;
    const activity = mergedActivities.find((a) => a.label === eventForm.activity) || mergedActivities[0];
    try {
      await api.post("/events", {
        dog_id: activeDog.id,
        event_date: format(selectedDate, "yyyy-MM-dd"),
        title: activity.label,
        event_type: activity.category,
        notes: eventForm.notes,
      });
      toast.success("Entry added");
      setEventForm({ activity: mergedActivities[0].label, notes: "" });
      setShowEventForm(false);
      setRunTick((t) => t + 1);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const removeEvent = async (id) => {
    await api.delete(`/events/${id}`);
    load();
  };

  const saveJournal = async () => {
    setSavingJournal(true);
    try {
      await api.post("/weekly-journal", { dog_id: activeDog.id, week_start: weekStartStr, content: journal });
      toast.success("Weekly journal saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingJournal(false);
    }
  };

  const selectedDs = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;
  const selectedEvents = selectedDs ? (eventsByDate[selectedDs] || []) : [];
  const totalEvents = events.length;
  const totalNotes = dailyEntries.length;
  const daysActive = vizData.filter((d) => d.events > 0 || d.note > 0).length;

  // ---- Today spotlight helpers ----
  const todayEvents = eventsByDate[todayStr] || [];
  const todayInWeek = todayStr >= weekStartStr && todayStr <= weekEndStr;

  const saveTodayNote = async () => {
    setSavingTodayNote(true);
    try {
      await api.post("/daily-entries", { dog_id: activeDog.id, entry_date: todayStr, content: todayNote });
      toast.success(todayNote.trim() ? "Note saved" : "Note cleared");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSavingTodayNote(false);
    }
  };

  const addTodayActivity = async () => {
    const activity = mergedActivities.find((a) => a.label === todayActivity) || mergedActivities[0];
    try {
      await api.post("/events", {
        dog_id: activeDog.id,
        event_date: todayStr,
        title: activity.label,
        event_type: activity.category,
        notes: "",
      });
      setRunTick((t) => t + 1);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  // ---- Hero stats ----
  const groomingCount = events.filter((e) => e.event_type === "grooming").length;
  const trainingCount = events.filter((e) => e.event_type === "training").length;
  const initials = (activeDog.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6" data-testid="weekly-planner-page">
      <DogRunAnimation trigger={runTick} />

      {/* View toggle: This dog (Week) ⇄ All dogs (Month) */}
      <div className="inline-flex rounded-md border border-border bg-card p-1" data-testid="weekly-view-toggle">
        <button
          type="button"
          onClick={() => setViewMode("this-dog")}
          data-testid="view-this-dog"
          className={`px-3 sm:px-4 py-1.5 rounded-sm text-sm font-medium transition-colors ${
            view === "this-dog"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          aria-pressed={view === "this-dog"}
        >
          This dog · Week
        </button>
        <button
          type="button"
          onClick={() => setViewMode("all-dogs")}
          data-testid="view-all-dogs"
          className={`px-3 sm:px-4 py-1.5 rounded-sm text-sm font-medium transition-colors ${
            view === "all-dogs"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          aria-pressed={view === "all-dogs"}
        >
          All dogs · Month
        </button>
      </div>

      {view === "all-dogs" ? <AllDogsMonthCalendar /> : (<>
      <SectionTitle
        overline="This week"
        title={`${format(weekStart, "MMM d")} – ${format(weekEnd, "MMM d, yyyy")}`}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setWeekStart(subWeeks(weekStart, 1))} data-testid="week-prev"><ChevronLeft size={16} /></Button>
            <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="week-this">This week</Button>
            <Button variant="outline" onClick={() => setWeekStart(addWeeks(weekStart, 1))} data-testid="week-next"><ChevronRight size={16} /></Button>
          </div>
        }
      />

      {/* Hero header */}
      <Card className="border-border shadow-none rounded-lg overflow-hidden bg-primary text-primary-foreground" data-testid="weekly-hero">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] md:grid-cols-[200px_1fr]">
            <div className="aspect-square sm:aspect-auto sm:h-full bg-primary-foreground/10 flex items-center justify-center">
              {activeDog.photo_url ? (
                <img src={activeDog.photo_url} alt={activeDog.name} className="w-full h-full object-cover" />
              ) : (
                <Avatar className="h-24 w-24 ring-2 ring-primary-foreground/30">
                  <AvatarFallback className="text-2xl font-display bg-primary-foreground/15 text-primary-foreground">{initials}</AvatarFallback>
                </Avatar>
              )}
            </div>
            <div className="p-6 sm:p-8 flex flex-col justify-center">
              <p className="text-[11px] uppercase tracking-[0.3em] opacity-70">Week of {format(weekStart, "MMM d, yyyy")}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1.5">{activeDog.name}</h1>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm opacity-90">
                <span><strong className="font-display text-lg">{totalEvents}</strong> entries</span>
                <span><strong className="font-display text-lg">{totalNotes}</strong> daily notes</span>
                <span><strong className="font-display text-lg">{daysActive}</strong>/7 active days</span>
                <span className="opacity-80">{groomingCount} grooming · {trainingCount} training</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Today spotlight */}
      {todayInWeek ? (
        <Card className="border-border shadow-none rounded-lg bg-secondary" data-testid="today-card">
          <CardContent className="p-6 sm:p-8 space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Sun size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Today</p>
                <h2 className="font-display text-2xl font-extrabold tracking-tighter">{format(today, "EEEE, MMMM d")}</h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Daily note (inline) */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Daily note</Label>
                <Textarea
                  rows={5}
                  value={todayNote}
                  onChange={(e) => setTodayNote(e.target.value)}
                  placeholder="A line or two about today…"
                  className="mt-1.5 bg-card text-base leading-7"
                  data-testid="today-note-textarea"
                />
                <div className="flex justify-end mt-2">
                  <Button onClick={saveTodayNote} disabled={savingTodayNote} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-today-note">
                    {savingTodayNote ? "Saving…" : "Save note"}
                  </Button>
                </div>
              </div>

              {/* Activities */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Today&apos;s activities</Label>
                    <button
                      type="button"
                      onClick={() => setManageActivitiesOpen(true)}
                      className="text-[11px] uppercase tracking-wider text-primary hover:underline"
                      data-testid="planner-manage-activities"
                    >
                      Manage
                    </button>
                  </div>
                  <div className="mt-1.5 flex gap-2">
                    <select
                      value={todayActivity}
                      onChange={(e) => setTodayActivity(e.target.value)}
                      className="flex-1 border border-border rounded-md bg-card px-3 py-2 text-sm"
                      data-testid="today-activity-select"
                    >
                      {mergedGroups.map((g) => (
                        <optgroup key={g.group} label={g.group}>
                          {g.items.map((a) => <option key={a.label} value={a.label}>{a.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    <Button onClick={addTodayActivity} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="today-add-activity">
                      <Plus size={14} className="mr-1" /> Add
                    </Button>
                  </div>
                  {todayEvents.length > 0 ? (
                    <ul className="mt-2.5 space-y-1.5">
                      {todayEvents.map((ev) => {
                        const meta = EVENT_TYPES.find((t) => t.value === ev.event_type) || EVENT_TYPES[3];
                        const Icon = meta.icon;
                        return (
                          <li key={ev.id} className="flex items-center gap-2 text-sm bg-card rounded-md px-3 py-1.5">
                            <Icon size={13} style={{ color: meta.color }} className="shrink-0" />
                            <span className="flex-1 truncate">{ev.title}</span>
                            <button onClick={() => removeEvent(ev.id)} className="text-muted-foreground hover:text-destructive p-0.5">
                              <Trash2 size={13} />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">No activities logged yet.</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Week grid */}
      <Card className="border-border shadow-none rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 md:grid-cols-7 divide-y md:divide-y-0 md:divide-x divide-border">
            {days.map((d) => {
              const ds = format(d, "yyyy-MM-dd");
              const dayEvents = eventsByDate[ds] || [];
              const dailyEntry = entryByDate[ds];
              return (
                <button
                  key={ds}
                  onClick={() => onDayClick(d)}
                  data-testid={`week-day-${ds}`}
                  className="text-left p-4 hover:bg-muted/40 transition-colors min-h-32 md:min-h-48"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{format(d, "EEE")}</p>
                      <p className={`text-lg font-medium ${isToday(d) ? "text-primary" : ""}`}>{format(d, "d")}</p>
                    </div>
                    {dailyEntry ? <NotebookPen size={14} className="text-primary" data-testid={`week-daily-${ds}`} /> : null}
                  </div>
                  {dailyEntry?.content ? (
                    <p className="text-[11px] text-foreground/80 line-clamp-3 italic mb-2">{dailyEntry.content}</p>
                  ) : null}
                  <div className="space-y-1">
                    {dayEvents.slice(0, 4).map((ev) => {
                      const meta = EVENT_TYPES.find((t) => t.value === ev.event_type) || EVENT_TYPES[3];
                      const Icon = meta.icon;
                      return (
                        <div key={ev.id} className="flex items-center gap-1.5 text-[11px] bg-card border border-border rounded px-1.5 py-0.5 truncate" title={ev.title}>
                          <Icon size={11} style={{ color: meta.color }} className="shrink-0" />
                          <span className="truncate">{ev.title}</span>
                        </div>
                      );
                    })}
                    {dayEvents.length > 4 ? <p className="text-[10px] text-muted-foreground">+{dayEvents.length - 4} more</p> : null}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <Card className="lg:col-span-2 border-border shadow-none rounded-lg" data-testid="weekly-viz">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Activity this week</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={vizData} stackOffset="sign">
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="grooming" stackId="a" fill={EVENT_TYPES[0].color} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="training" stackId="a" fill={EVENT_TYPES[1].color} />
                  <Bar dataKey="vet" stackId="a" fill={EVENT_TYPES[2].color} />
                  <Bar dataKey="other" stackId="a" fill={EVENT_TYPES[3].color} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="note" fill="hsl(120 8% 70%)" radius={[4, 4, 4, 4]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
              {EVENT_TYPES.map((t) => (
                <div key={t.value} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: t.color }} />
                  {t.label}
                </div>
              ))}
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
                Daily note
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-4">
          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Events</p>
              <p className="font-display text-3xl font-extrabold tracking-tighter mt-1">{totalEvents}</p>
            </CardContent>
          </Card>
          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Daily notes</p>
              <p className="font-display text-3xl font-extrabold tracking-tighter mt-1">{totalNotes}</p>
            </CardContent>
          </Card>
          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Active days</p>
              <p className="font-display text-3xl font-extrabold tracking-tighter mt-1">{daysActive} / 7</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Weekly journal */}
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Weekly journal</p>
              <h3 className="font-display text-xl font-bold tracking-tight">Reflections & wins</h3>
            </div>
          </div>
          <Textarea
            value={journal}
            onChange={(e) => setJournal(e.target.value)}
            rows={10}
            placeholder="What went well this week? Training breakthroughs, health observations, things to remember…"
            className="text-base leading-7"
            data-testid="weekly-journal-textarea"
          />
          <div className="flex justify-end mt-4">
            <Button onClick={saveJournal} disabled={savingJournal} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-weekly-journal-button">
              {savingJournal ? "Saving…" : "Save journal"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Day dialog */}
      </>)}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="day-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">
              {selectedDate ? format(selectedDate, "EEEE, MMM d") : ""}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="daily-note">Daily note</Label>
            <Textarea
              id="daily-note"
              data-testid="daily-note-textarea"
              rows={4}
              value={dailyText}
              onChange={(e) => setDailyText(e.target.value)}
              placeholder="How did the day go?"
            />
            <div className="flex justify-end">
              <Button onClick={saveDaily} disabled={savingDaily} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-daily-note-button">
                {savingDaily ? "Saving…" : "Save note"}
              </Button>
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-sm">Events</p>
              {!showEventForm ? (
                <Button variant="outline" size="sm" onClick={() => setShowEventForm(true)} data-testid="show-add-event-form">
                  <Plus size={14} className="mr-1" /> Add event
                </Button>
              ) : null}
            </div>

            {selectedEvents.length === 0 && !showEventForm ? (
              <p className="text-xs text-muted-foreground">No events scheduled.</p>
            ) : null}

            {selectedEvents.length > 0 ? (
              <ul className="space-y-1.5">
                {selectedEvents.map((ev) => {
                  const meta = EVENT_TYPES.find((t) => t.value === ev.event_type) || EVENT_TYPES[3];
                  const Icon = meta.icon;
                  return (
                    <li key={ev.id} className="flex items-start gap-2 text-sm bg-muted/40 rounded-md px-3 py-2">
                      <Icon size={14} style={{ color: meta.color }} className="mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{ev.title}</p>
                        {ev.notes ? <p className="text-xs text-muted-foreground">{ev.notes}</p> : null}
                      </div>
                      <button onClick={() => removeEvent(ev.id)} className="text-muted-foreground hover:text-destructive p-1">
                        <Trash2 size={13} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {showEventForm ? (
              <form onSubmit={addEvent} className="space-y-3 bg-secondary/40 rounded-md p-3">
                <div>
                  <Label className="text-xs">Activity</Label>
                  <select
                    value={eventForm.activity}
                    onChange={(e) => setEventForm({ ...eventForm, activity: e.target.value })}
                    className="mt-1 w-full border border-border rounded-md bg-card px-3 py-2 text-sm"
                    data-testid="event-activity-select"
                  >
                    {mergedGroups.map((g) => (
                      <optgroup key={g.group} label={g.group}>
                        {g.items.map((a) => <option key={a.label} value={a.label}>{a.label}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Notes (optional)</Label>
                  <Textarea
                    rows={2}
                    value={eventForm.notes}
                    onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowEventForm(false)}>Cancel</Button>
                  <Button type="submit" size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="submit-event-button">Add entry</Button>
                </div>
              </form>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom activities manager (per active dog) */}
      {activeDog ? (
        <CustomActivitiesDialog
          open={manageActivitiesOpen}
          onOpenChange={setManageActivitiesOpen}
          dogId={activeDog.id}
          dogName={activeDog.name}
          onChanged={reloadCustomActivities}
        />
      ) : null}
    </div>
  );
}
